import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ShieldCheck, Plus, Trash, DownloadSimple, MagnifyingGlass, CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";

/* Base de datos revisada de clientes/proveedores + verificación de identificación de facturas. */
export default function TercerosMaestro({ clienteId, onFacturasChanged }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [form, setForm] = useState({ cif: "", nombre: "", tipo: "proveedor" });
    const [result, setResult] = useState(null);
    const [filter, setFilter] = useState("");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/asesor/clientes/${clienteId}/terceros`);
            setItems(data.items || []);
        } catch (e) { setError(formatApiError(e, "No se pudo cargar la base revisada")); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [clienteId]); // eslint-disable-line

    async function importar() {
        setBusy("importar"); setError(""); setInfo("");
        try {
            const { data } = await api.post(`/asesor/clientes/${clienteId}/terceros/importar-directorio`);
            setInfo(`Importados ${data.creados} terceros desde las facturas confirmadas (${data.total} en total).`);
            await load();
        } catch (e) { setError(formatApiError(e, "No se pudo importar el directorio")); }
        finally { setBusy(""); }
    }

    async function add(e) {
        e.preventDefault();
        setBusy("add"); setError("");
        try {
            await api.post(`/asesor/clientes/${clienteId}/terceros`, form);
            setForm({ cif: "", nombre: "", tipo: "proveedor" });
            await load();
        } catch (e2) { setError(formatApiError(e2, "No se pudo guardar el tercero")); }
        finally { setBusy(""); }
    }

    async function remove(t) {
        if (!window.confirm(`¿Eliminar «${t.nombre}» (${t.cif}) de la base revisada?`)) return;
        try { await api.delete(`/asesor/terceros/${t.id}`); await load(); }
        catch (e) { setError(formatApiError(e, "No se pudo eliminar")); }
    }

    async function verificar() {
        setBusy("verificar"); setError(""); setResult(null);
        try {
            const { data } = await api.post(`/asesor/clientes/${clienteId}/terceros/verificar`);
            setResult(data);
            if (onFacturasChanged) onFacturasChanged();
        } catch (e) { setError(formatApiError(e, "No se pudo verificar")); }
        finally { setBusy(""); }
    }

    async function aplicar(d, s) {
        try {
            await api.post(`/asesor/facturas/${d.factura_id}/aplicar-sugerencia`, { campo: s.campo, valor: s.propuesto });
            setResult((r) => r && { ...r, discrepancias: r.discrepancias.map((x) => x.factura_id !== d.factura_id ? x : { ...x, sugerencias: x.sugerencias.filter((y) => y !== s), aplicadas: (x.aplicadas || 0) + 1 }) });
            if (onFacturasChanged) onFacturasChanged();
        } catch (e) { setError(formatApiError(e, "No se pudo aplicar la corrección")); }
    }

    const visibles = items.filter((t) => !filter || `${t.nombre} ${t.cif} ${(t.alias || []).join(" ")}`.toLowerCase().includes(filter.toLowerCase()));
    const pendientes = (result?.discrepancias || []).filter((d) => d.sugerencias.length > 0);

    return (
        <div className="gf-card mb-6" data-testid="terceros-maestro">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                    <div className="micro-label mb-1">— Base de datos revisada</div>
                    <h2 className="serif text-2xl inline-flex items-center gap-2"><ShieldCheck size={22} weight="fill" className="text-[var(--gf-gold)]" /> Clientes y proveedores verificados</h2>
                    <p className="text-xs text-[var(--gf-text-secondary)] mt-1 max-w-2xl">
                        Con esta base, cada factura OCR se coteja automáticamente: si el CIF coincide pero el nombre no (o al revés), el sistema avisa y propone la corrección.
                        Se aplica a las nuevas subidas y, con «Verificar facturas», a las ya cargadas.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={importar} disabled={!!busy} className="btn-outline text-sm" data-testid="btn-importar-directorio">
                        <DownloadSimple size={14} /> {busy === "importar" ? "Importando…" : "Crear desde facturas confirmadas"}
                    </button>
                    <button onClick={verificar} disabled={!!busy || items.length === 0} className="btn-gold text-sm" data-testid="btn-verificar-facturas">
                        <MagnifyingGlass size={14} weight="bold" /> {busy === "verificar" ? "Verificando…" : "Verificar facturas"}
                    </button>
                </div>
            </div>

            {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 mb-3" data-testid="terceros-error">{error}</div>}
            {info && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 mb-3" data-testid="terceros-info">{info}</div>}

            {result && (
                <div className="border border-[var(--gf-border)] bg-[var(--gf-cream)] p-4 mb-4" data-testid="verificacion-resultado">
                    <div className="flex flex-wrap gap-4 text-sm mb-3">
                        <span><strong>{result.revisadas}</strong> facturas revisadas</span>
                        <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" /> <strong>{result.coinciden}</strong> coinciden</span>
                        <span className="text-amber-700 inline-flex items-center gap-1"><Warning size={14} weight="fill" /> <strong data-testid="num-discrepancias">{pendientes.length}</strong> con discrepancias</span>
                        <span className="text-[var(--gf-text-secondary)]"><strong>{result.terceros_nuevos}</strong> terceros nuevos (no están en la base)</span>
                    </div>
                    {pendientes.length === 0 ? (
                        <div className="text-xs text-emerald-800" data-testid="sin-discrepancias">Sin discrepancias de identificación pendientes.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="data-table w-full text-xs" data-testid="tabla-discrepancias">
                                <thead><tr><th>Factura</th><th>Contraparte en factura</th><th>Corrección propuesta</th><th className="text-right">Acción</th></tr></thead>
                                <tbody>
                                    {pendientes.map((d) => d.sugerencias.map((s, i) => (
                                        <tr key={`${d.factura_id}-${i}`} data-testid={`discrepancia-${d.factura_id}-${s.campo}`}>
                                            <td className="mono">{d.numero || d.archivo || d.factura_id.slice(0, 8)}<div className="text-[10px] text-[var(--gf-text-muted)]">{d.fecha} · {d.tipo}</div></td>
                                            <td>{d.nombre_actual || <em>sin nombre</em>}<div className="mono text-[10px]">{d.cif_actual || "sin CIF"}</div></td>
                                            <td>
                                                <span className="text-[var(--gf-text-muted)] line-through mr-1">{s.actual || "∅"}</span>
                                                <ArrowRight size={10} className="inline" /> <strong className="text-[var(--gf-navy)]">{s.propuesto}</strong>
                                                <div className="text-[10px] text-[var(--gf-text-secondary)]">{s.motivo}</div>
                                            </td>
                                            <td className="text-right">
                                                <button onClick={() => aplicar(d, s)} className="btn-gold text-[11px] py-1 px-2" data-testid={`aplicar-${d.factura_id}-${s.campo}`}>Aplicar</button>
                                            </td>
                                        </tr>
                                    )))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-[160px_1fr_150px_auto] gap-2 mb-4" data-testid="form-tercero">
                <input className="gf-input text-sm" placeholder="CIF / NIF" value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} required data-testid="tercero-cif" />
                <input className="gf-input text-sm" placeholder="Nombre oficial del tercero" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required data-testid="tercero-nombre" />
                <select className="gf-input text-sm" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} data-testid="tercero-tipo">
                    <option value="proveedor">Proveedor</option>
                    <option value="cliente">Cliente</option>
                    <option value="ambos">Ambos</option>
                </select>
                <button type="submit" disabled={!!busy} className="btn-outline text-sm" data-testid="btn-add-tercero"><Plus size={14} /> Añadir</button>
            </form>

            <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-xs text-[var(--gf-text-secondary)]" data-testid="terceros-count">{items.length} terceros en la base revisada</div>
                <input className="gf-input text-xs py-1 w-56" placeholder="Buscar…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="terceros-filter" />
            </div>
            {loading ? (
                <div className="text-sm text-[var(--gf-text-secondary)] py-4">Cargando…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-[var(--gf-text-secondary)] py-6 text-center border border-dashed border-[var(--gf-border)]" data-testid="terceros-empty">
                    Aún no hay base revisada. Pulsa «Crear desde facturas confirmadas» o añade terceros a mano.
                </div>
            ) : (
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                    <table className="data-table w-full text-sm" data-testid="tabla-terceros">
                        <thead><tr><th>Nombre oficial</th><th>CIF</th><th>Tipo</th><th>Alias detectados</th><th></th></tr></thead>
                        <tbody>
                            {visibles.map((t) => (
                                <tr key={t.id} data-testid={`tercero-${t.cif}`}>
                                    <td className="font-medium">{t.nombre}</td>
                                    <td className="mono text-xs">{t.cif}</td>
                                    <td className="text-xs capitalize">{t.tipo}</td>
                                    <td className="text-[11px] text-[var(--gf-text-secondary)]">{(t.alias || []).join(" · ") || "—"}</td>
                                    <td className="text-right">
                                        <button onClick={() => remove(t)} className="text-[var(--gf-text-muted)] hover:text-red-600" title="Eliminar" data-testid={`del-tercero-${t.cif}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
